import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';
import { isUndefined } from 'lodash';

import { NavLinkButton } from '@shared/components/bootstrap';
import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';
import { Panel } from '@shared/components/bootstrap/panel';
import { Currency } from '@shared/components/helpers';
import { useBooleanURLSearchParam } from '@shared/hooks/use_boolean_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { EstimatorSignatureAffirmation } from '@admin/components/estimator_signature_affirmation';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Titleize } from '@admin/components/helpers/titleize';
import { Fetcher as InventoryFetcher } from '@admin/components/inventory/fetcher';
import { MovingAppointmentDetails } from '@admin/components/moving/appointment_details/appointment_details';
import { OrderAddressSelector } from '@admin/components/orders/address_selector';
import { Cancellation as CancellationDisclaimer } from '@admin/components/orders/disclaimers/cancellation';
import { TermContracts as OrderTermContractsDisclaimer } from '@admin/components/orders/disclaimers/term_contracts';
import { FinalReturnCancellationPanel } from '@admin/components/orders/final_return_cancellation_panel';
import { InventorySection as OrderInventorySection } from '@admin/components/orders/inventory_section';
import { MovingAddressSelectors } from '@admin/components/orders/moving/address_selectors';
import { STORAGE_UNIT_FIELDS } from '@admin/components/orders/moving/selection_details_summary';
import { OrderSharedFields } from '@admin/components/orders/shared_fields';
import { WarehouseReturnServiceTypeSelector } from '@admin/components/orders/warehouse_return_service_type_selector';
import { Spinner } from '@admin/components/spinner';
import { SubscribePanel } from '@admin/components/subscribe/panel';
import { accountURL, orderURL } from '@admin/config/routes';
import { MOVE_RESCHEDULE_FEE_DESCRIPTION, type OrderFormType } from '@admin/constants/order_form_attributes';
import { useAccountMoveRequest } from '@admin/hooks/use_account_move_request';
import { useAccountPickupRequest } from '@admin/hooks/use_account_pickup_request';
import { useAccountRequest } from '@admin/hooks/use_account_request';
import { useAccountRetailDeliveryRequest } from '@admin/hooks/use_account_retail_delivery_request';
import { useAccountReturnRequest } from '@admin/hooks/use_account_return_request';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import {
  classifyOrderSubtype,
  estimateReturnMovers,
  fetchAccountOnboarded,
  orderFormErrorMessage,
  saveOrder,
} from '@admin/utils/order_form_api';

declare const window: {
  clutter?: {
    user?: {
      roles?: string[];
    };
  };
};

const DEFAULT_RESCHEDULE_FEE_TITLE = 'Are you sure you want to reschedule with the $100 reschedule fee?';
const DEFAULT_RESCHEDULE_FEE_DESCRIPTION =
  'This order was previously rescheduled by the customer within ' +
  '48 hours of the job, this will incur a $100 cancellation fee';

const MOVE_RESCHEDULE_FEE_TITLE = 'Are you sure you want to reschedule?';

const SIMULTANEOUS_ORDER_TITLE = 'Are you sure you want to change this order along with the linked order?';
const SIMULTANEOUS_ORDER_DESCRIPTION = 'This order is linked to another order. Changing it will change both orders.';

const WAREHOUSE_ORDER_TITLE = 'Are you sure you don’t want to book this order in the Account Portal?';
const WAREHOUSE_ORDER_DESCRIPTION =
  'Disposals and Facility Pickups should be booked via the Account Portal unless this a special case that does not require customer billing.';

const WAREHOUSE_RETURN_SERVICE_TYPES = ['auction', 'disposal', 'third_party_return'];
const MOVING_SQUARE_FOOTAGE_BUILDING_TYPES = ['Commercial', 'Other'];
const MOVING_CUSTOM_UNIT_BUILDING_TYPES = ['Studio', 'Storage Facility / Warehouse'];
const MOVE_SIZE_BUILDING_TYPES = [...MOVING_SQUARE_FOOTAGE_BUILDING_TYPES, ...MOVING_CUSTOM_UNIT_BUILDING_TYPES];
const STORAGE_UNIT_SIZES = new Set(STORAGE_UNIT_FIELDS);

const RETURN_ITEM_ALERT_STATES = ['approved', 'pending', 'prepared'];

type OrderRecord = Record<string, any>;

type AccountRecord = Record<string, any>;

const asOrderFormAccount = (account: AccountRecord): any => account;

const asOrderFormOrder = (order: OrderRecord): any => order;

const hasRole = (...roles: string[]) => {
  const userRoles = window.clutter?.user?.roles || [];
  return roles.some((role) => userRoles.includes(role));
};

const isCheckboxChecked = (id: string) => {
  if (typeof document === 'undefined') return false;
  return !!(document.getElementById(id) as HTMLInputElement | null)?.checked;
};

const orderTypeFormatted = (order: OrderRecord, formType: OrderFormType) => {
  const type = order.type || formType;
  switch (type) {
    case 'selfstorage-shuttle':
      return 'Self-Storage Shuttle';
    case 'moving-move':
    case 'move':
      return 'Move';
    case 'retail-delivery':
      return 'Retail Delivery';
    default:
      return type;
  }
};

export const OrderForm: React.FC<{
  account: AccountRecord;
  order: OrderRecord;
  formType: OrderFormType;
  external?: boolean;
  warehouseReturn?: boolean;
  onboardingDate?: string;
}> = ({
  account,
  order,
  formType,
  external = true,
  warehouseReturn: warehouseReturnParam,
  onboardingDate: onboardingDateParam,
}) => {
  const navigate = useNavigate();
  const { confirmation, confirm } = useConfirmation();

  if (!order.type) {
    order.type = formType;
  }

  const [disclaimer, setDisclaimer] = useState(false);
  const [disclaimerForMaterialsBundle, setDisclaimerForMaterialsBundle] = useState(false);
  const [estimatorAffirmation, setEstimatorAffirmation] = useState(false);
  const [isEarlyTermination, setIsEarlyTermination] = useState<boolean | undefined>();
  const [earlyTerminationDisclaimer, setEarlyTerminationDisclaimer] = useState(false);
  const [estimatedCUFT, setEstimatedCUFT] = useState<number | undefined>(order.estimated_cuft);
  const [estimatedMovers, setEstimatedMovers] = useState<number | undefined>(
    order.movers != null && order.movers > 0 ? order.movers : 1,
  );
  const [saving, setSaving] = useState(false);
  const [onboardingDate, setOnboardingDate] = useState<string | undefined>(onboardingDateParam);
  const [movingSquareFootage, setMovingSquareFootage] = useState(0);
  const [storageUnitSize, setStorageUnitSize] = useState<string | undefined>();
  const [renderVersion, setRenderVersion] = useState(0);
  const bumpRender = useCallback(() => setRenderVersion((value) => value + 1), []);
  const previousFollowUpJob = useRef(order.follow_up_job);

  const warehouseReturn = useCallback(() => {
    if (warehouseReturnParam) return true;
    return WAREHOUSE_RETURN_SERVICE_TYPES.includes(order.service_type);
  }, [order.service_type, warehouseReturnParam]);

  const isReonboarding = useCallback(
    () => !order.id && order.type === 'pickup' && order.subtype === 'onboarding' && external,
    [order.id, order.type, order.subtype, external],
  );

  const onRoomsChange = useCallback(
    (field: string, value: number | boolean) => {
      let quantity: number | boolean = value;
      if (typeof value === 'boolean') {
        quantity = value ? 1 : 0;
      }

      const existingSelection = order.moving_selections?.find(
        ({ moving_category }: { moving_category: { name: string } }) => moving_category.name === field,
      );

      if (existingSelection) {
        existingSelection.quantity = quantity;
      } else {
        if (!order.moving_selections) {
          order.moving_selections = [];
        }
        order.moving_selections.push({ quantity, moving_category: { name: field } });
      }
      bumpRender();
    },
    [order, bumpRender],
  );

  const clearMovingSelections = useCallback(() => {
    order.moving_selections = order.moving_selections?.map((movingSelection: OrderRecord) => ({
      ...movingSelection,
      quantity: 0,
    }));
    bumpRender();
  }, [order, bumpRender]);

  const onMovingSquareFootageChange = useCallback(
    (squareFootage: number) => {
      setMovingSquareFootage(squareFootage);
      clearMovingSelections();
      onRoomsChange('square_footage', squareFootage);
    },
    [clearMovingSelections, onRoomsChange],
  );

  const onStorageUnitSizeChange = useCallback(
    (unitSize: string) => {
      setStorageUnitSize(unitSize);
      clearMovingSelections();
      onRoomsChange(unitSize, 1);
    },
    [clearMovingSelections, onRoomsChange],
  );

  const runEstimateReturnMovers = useCallback(async () => {
    if (order.movers_overwritten_at != null) return;
    const itemIds = order.items?.map((item: { id: number }) => item.id) || [];
    const movers = await estimateReturnMovers({
      itemIds,
      addressId: order.address?.id,
    });
    order.movers = movers;
    bumpRender();
  }, [order, bumpRender]);

  useEffect(() => {
    order.override_movers = order.movers_overwritten_at != null;

    if (onboardingDateParam) {
      order.follow_up_job = true;
      setOnboardingDate(DateTime.fromISO(onboardingDateParam).toFormat('EEEE, MMMM d, yyyy'));
    }

    if (order.type === 'moving-move') {
      const accountPackages = order.account_packages || [];
      const movingSelections = order.moving_selections || [];

      if (order.moving_operation) {
        order.moving_operation.packing_supplies_required = accountPackages.some(
          (accountPackage: { kind: string }) => accountPackage.kind === 'packing_supplies',
        );
      }

      order.materials_package_set_entry_id = accountPackages.find(
        (accountPackage: { kind: string }) => accountPackage.kind === 'packing_supplies',
      )?.package_set_entry_id;

      setMovingSquareFootage(
        movingSelections.find(({ moving_category }: OrderRecord) => moving_category.name === 'square_footage')
          ?.quantity || 0,
      );
      setStorageUnitSize(
        movingSelections.find(
          ({ quantity, moving_category }: OrderRecord) => STORAGE_UNIT_SIZES.has(moving_category.name) && quantity > 0,
        )?.moving_category?.name,
      );
      if (order.scheduled) {
        setOnboardingDate(DateTime.fromJSDate(new Date(order.scheduled)).toFormat('EEEE, MMMM d, yyyy'));
      }
    }

    if (warehouseReturn()) {
      const defaultDate = new Date();
      defaultDate.setHours(10, 0, 0, 0);
      if (!order.scheduled) {
        order.scheduled = defaultDate;
      }
    }

    const classify = async () => {
      if (!order.id) {
        order.type = formType;
        const response = await classifyOrderSubtype({
          accountID: account.id,
          formType,
          cancelable: order.cancel_account,
          subtype: order.subtype,
        });
        if (response.subtype) {
          order.subtype = response.subtype;
          bumpRender();
        }
      }

      if (order.subtype === 'onboarding') {
        order.reonboarding = await fetchAccountOnboarded(account.id);
        bumpRender();
      }
    };

    classify();

    if (formType === 'pickup') {
      if (order.estimated_cuft != null) {
        setEstimatedCUFT(order.estimated_cuft);
      }
      setEstimatedMovers(order.movers != null && order.movers > 0 ? order.movers : 1);
      if (!order.id && order.full_move_out == null) {
        order.full_move_out = false;
        if (order.full_pack == null) {
          order.full_pack = false;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentFollowUpJob = order.follow_up_job;
    if (currentFollowUpJob === previousFollowUpJob.current) {
      return;
    }
    if (currentFollowUpJob !== true) {
      order.predecessor_id = null;
    }
    previousFollowUpJob.current = currentFollowUpJob;
  }, [order.follow_up_job, order]);

  useEffect(() => {
    if (formType === 'return' && order.address) {
      runEstimateReturnMovers();
    }
  }, [formType, order.address?.id, runEstimateReturnMovers]);

  useEffect(() => {
    if (order.override_movers === true) {
      order.movers_overwritten_at = DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');
      bumpRender();
    } else if (order.override_movers === false) {
      order.mover_override_reason = null;
      order.movers_overwritten_at = null;
      if (formType === 'return') {
        runEstimateReturnMovers();
      } else {
        order.movers = estimatedMovers;
      }
      bumpRender();
    }
  }, [order.override_movers, formType, estimatedMovers, runEstimateReturnMovers, bumpRender, order]);

  useEffect(() => {
    const buildingType = order.moving_operation?.origin_address?.building_type;
    if (order.type !== 'moving-move' || !buildingType) return;

    const moveSizeNotSet = !order.moving_operation.move_size;
    if (!MOVE_SIZE_BUILDING_TYPES.includes(buildingType)) {
      delete order.moving_operation.move_size;
    } else if (moveSizeNotSet) {
      order.moving_operation.move_size = 'EVERYTHING';
    }

    if (!MOVING_SQUARE_FOOTAGE_BUILDING_TYPES.includes(buildingType) && movingSquareFootage > 0) {
      onMovingSquareFootageChange(0);
    }

    if (buildingType === 'Storage Facility / Warehouse') {
      onStorageUnitSizeChange(storageUnitSize || 'locker');
    } else if (storageUnitSize) {
      clearMovingSelections();
    }

    if (buildingType === 'Studio') {
      clearMovingSelections();
      onRoomsChange('studio', 1);
    }
    bumpRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.moving_operation?.origin_address?.building_type, order.type]);

  useEffect(() => {
    if (formType !== 'return') return;

    const classify = async () => {
      const response = await classifyOrderSubtype({
        accountID: account.id,
        formType,
        cancelable: order.cancel_account,
        subtype: order.subtype,
      });
      if (response.subtype) {
        order.subtype = response.subtype;
        bumpRender();
      }
    };

    classify();
    runEstimateReturnMovers();
  }, [
    order.items,
    formType,
    account.id,
    order.cancel_account,
    order.subtype,
    runEstimateReturnMovers,
    bumpRender,
    order,
  ]);

  const standardSavables = useCallback(() => {
    const movingAttributesValid =
      !MOVING_SQUARE_FOOTAGE_BUILDING_TYPES.includes(order.moving_operation?.origin_address?.building_type) ||
      movingSquareFootage > 0;

    const disclaimerAccepted =
      disclaimer ||
      isCheckboxChecked('disclaimer') ||
      warehouseReturn() ||
      order.type === 'selfstorage-shuttle' ||
      order.type === 'retail-delivery';

    const estimatorAffirmed = estimatorAffirmation || isCheckboxChecked('estimator-signature-affirmation');
    const estimatorRequiredAndProvided = !!order.id !== !!estimatorAffirmed;

    return (
      !saving &&
      !!order &&
      disclaimerAccepted &&
      (!order.unpaid_materials_bundle_amount || disclaimerForMaterialsBundle) &&
      (!order.rescheduling || order.rescheduler) &&
      estimatorRequiredAndProvided &&
      (!order.scheduled_override || order.scheduled_override_reason) &&
      (!order.movers_overwritten_at || order.mover_override_reason) &&
      (!warehouseReturn() || (warehouseReturn() && WAREHOUSE_RETURN_SERVICE_TYPES.includes(order.service_type))) &&
      (order.type !== 'moving-move' || movingAttributesValid) &&
      (order.type !== 'selfstorage-shuttle' || order.id || (order.reservation && order.reservation.id)) &&
      (!order.follow_up_job_exception || order.follow_up_job_exception_reason)
    );
  }, [
    saving,
    order,
    disclaimer,
    warehouseReturn,
    disclaimerForMaterialsBundle,
    estimatorAffirmation,
    movingSquareFootage,
  ]);

  const savable = useMemo(() => {
    if (order.cancel_account) {
      return standardSavables() && (!isEarlyTermination || isEarlyTermination === !!earlyTerminationDisclaimer);
    }
    return standardSavables();
  }, [order.cancel_account, standardSavables, isEarlyTermination, earlyTerminationDisclaimer, renderVersion]);

  const onInventory = useCallback(
    (inventory: OrderRecord) => {
      const isFullMoveOut = inventory.isFullMoveOut ?? order.full_move_out ?? false;
      const needsPackingHelp = inventory.needsPackingHelp ?? false;
      Object.assign(order, {
        inventory,
        full_move_out: isFullMoveOut,
        full_pack: isFullMoveOut || needsPackingHelp,
        requested_movers: inventory.requestedMovers ?? order.requested_movers ?? null,
      });
      bumpRender();
    },
    [order, bumpRender],
  );

  const onItems = useCallback(
    (selections: OrderRecord[], requestables: OrderRecord[]) => {
      order.items = selections;
      order.allItemsSelected = requestables.length === selections.length;
      bumpRender();
    },
    [order, bumpRender],
  );

  const onSubscribe = useCallback(
    ({
      labor,
      laborRate,
      storage,
      protection,
      length,
      width,
      quantity,
      activePricingSet,
      accountPackages,
      appointmentQuote,
    }: OrderRecord) => {
      if (!labor || !laborRate || !storage || !protection || !activePricingSet) {
        delete order.subscribe_attributes;
        delete order.account_packages_attributes;
      } else {
        Object.assign(order, {
          subscribe_attributes: {
            quote_id: activePricingSet.quoteId,
            materials_bundle_purchase: true,
            pricing_set_id: activePricingSet.id,
            labor_pricing_group_entry_id: labor.id,
            storage_pricing_group_entry_id: storage.id,
            protection_pricing_group_entry_id: protection.id,
            labor_rate_id: laborRate.id,
            appointment_quote_id: appointmentQuote?.id,
            storage_attributes: {
              length,
              width,
              quantity,
            },
          },
          account_packages_attributes: accountPackages,
        });
        if (!warehouseReturn()) {
          if (labor.rateGroup?.curbside) {
            order.service_type = 'curbside_pickup';
          } else {
            order.service_type = 'full_service';
          }
        }
      }
      bumpRender();
    },
    [order, warehouseReturn, bumpRender],
  );

  const onWarehouseReturnServiceTypeSelection = useCallback(
    (serviceType: string) => {
      order.service_type = serviceType;
      switch (serviceType) {
        case 'auction':
        case 'disposal':
          order.external = false;
          break;
        default:
          order.external = true;
      }
      bumpRender();
    },
    [order, bumpRender],
  );

  const setAccountCancelIntentReason = useCallback(
    (reason: string) => {
      if (!order.account_cancel_intent) {
        order.account_cancel_intent = {};
      }
      order.account_cancel_intent.reason = reason;
      bumpRender();
    },
    [order, bumpRender],
  );

  const clearAccountCancelIntentReason = useCallback(() => {
    order.account_cancel_intent = {};
    bumpRender();
  }, [order, bumpRender]);

  const setCancelAccount = useCallback(
    (value: boolean) => {
      order.cancel_account = value;
      bumpRender();
    },
    [order, bumpRender],
  );

  const onAddressSelect = useCallback(
    (address: OrderRecord) => {
      order.address = address;
      bumpRender();
    },
    [order, bumpRender],
  );

  const onRegionChange = useCallback(
    (region: OrderRecord) => {
      order.region = region;
      bumpRender();
    },
    [order, bumpRender],
  );

  const onEstimateCubicFootage = useCallback((value: number) => {
    setEstimatedCUFT(value);
  }, []);

  const onMoverCountUpdate = useCallback(
    (movers: number) => {
      setEstimatedMovers(movers);
      if (order.movers_overwritten_at == null) {
        order.movers = movers;
        bumpRender();
      }
    },
    [order, bumpRender],
  );

  const runConfirmations = async () => {
    if (!order.cardless && order.rescheduled_at && order.rescheduler && order.rescheduler.type === 'Customer') {
      let confirmTitle = DEFAULT_RESCHEDULE_FEE_TITLE;
      let description = DEFAULT_RESCHEDULE_FEE_DESCRIPTION;

      if (order.type === 'moving-move') {
        confirmTitle = MOVE_RESCHEDULE_FEE_TITLE;
        description = `I have informed the customer that: "${MOVE_RESCHEDULE_FEE_DESCRIPTION}"`;
      }

      const response = await confirm({ title: confirmTitle, description });
      if (response !== ConfirmationResponse.Confirm) return false;
    }

    if (order.simultaneous) {
      const response = await confirm({
        title: SIMULTANEOUS_ORDER_TITLE,
        description: SIMULTANEOUS_ORDER_DESCRIPTION,
      });
      if (response !== ConfirmationResponse.Confirm) return false;
    }

    if (['disposal', 'third_party_return'].includes(order.service_type)) {
      const response = await confirm({
        title: WAREHOUSE_ORDER_TITLE,
        description: WAREHOUSE_ORDER_DESCRIPTION,
      });
      if (response !== ConfirmationResponse.Confirm) return false;
    }

    return true;
  };

  const showError = async (message: string) => {
    await confirm({
      title: 'Whoops',
      description: message,
      confirm: 'OK',
      showCancel: false,
    });
  };

  const handleSave = async () => {
    if (hasRole('l1_agent')) {
      await showError('You do not have permission to update or schedule orders.');
      return;
    }

    if (isReonboarding() && !order.subscribe_attributes) {
      await showError('Please select a valid storage and protection plan.');
      return;
    }

    if (order.follow_up_job && !order.predecessor_id) {
      await showError('Please select a valid parent order.');
      return;
    }

    if (!order.id && order.type === 'pickup' && isUndefined(order.full_move_out)) {
      await showError('Please specify if this is a full move-out before saving the order!');
      return;
    }

    if (!order.address && order.type !== 'moving-move') {
      await showError('Please select an address before saving the order!');
      return;
    }

    if (order.cancel_account && !order.account_cancel_intent?.reason) {
      await showError('Please select a cancellation reason.');
      return;
    }

    if (!savable) return;

    const confirmed = await runConfirmations();
    if (!confirmed) return;

    setSaving(true);
    try {
      const saved = await saveOrder({ formType, accountID: account.id, order });
      navigate(orderURL({ id: saved.id, account: { id: saved.account_id } }));
    } catch (error) {
      await showError(orderFormErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const isAllowedToAddItemsToEnroute = hasRole('admin') || (hasRole('manager') && hasRole('warehouse'));
  const showReturnItemAlert = formType === 'return' && order.state && !RETURN_ITEM_ALERT_STATES.includes(order.state);
  const showSubscribePanel = (order.external && order.subtype === 'onboarding') || isReonboarding();

  const submitLabel = order.state === 'pending' ? 'Approve and Notify Customer' : order.id ? 'Update' : 'Schedule';

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>
          {account.id && (
            <Breadcrumb.Item>
              <NavLink to={accountURL({ id: account.id })}>
                #{account.number} ({account.customer.name})
              </NavLink>
            </Breadcrumb.Item>
          )}
          {account.id && order.id && (
            <Breadcrumb.Item>
              <NavLink to={orderURL({ id: order.id, account: { id: account.id } })}>#{order.number}</NavLink>
            </Breadcrumb.Item>
          )}
          {account.id && order.id && <Breadcrumb.Item active> Edit</Breadcrumb.Item>}
          {account.id && !order.id && (
            <Breadcrumb.Item active>
              <Titleize text={orderTypeFormatted(order, formType)} /> - New
            </Breadcrumb.Item>
          )}
        </Breadcrumb>
      </PageHeader>

      <form role="form">
        <Panel>
          <Panel.Header>
            <h3 className="panel-title">
              Order » <Titleize text={orderTypeFormatted(order, formType)} />
            </h3>
          </Panel.Header>
          <Panel.Body>
            {order.type !== 'moving-move' ? (
              <OrderAddressSelector
                account={asOrderFormAccount(account)}
                order={asOrderFormOrder(order)}
                showAccountAddresses={order.external || order.type === 'return'}
                warehouseReturn={warehouseReturn()}
                onAddressSelect={onAddressSelect}
                onRegionChange={(region) => onRegionChange(region as OrderRecord)}
              />
            ) : (
              <MovingAddressSelectors account={asOrderFormAccount(account)} order={asOrderFormOrder(order)} />
            )}

            <hr />

            {order.type === 'moving-move' && order.id && (
              <div>
                <MovingAppointmentDetails orderID={String(order.id)} />
              </div>
            )}

            {formType === 'pickup' && (
              <>
                <h3 className="text-thin">Inventory List</h3>
                <OrderInventorySection
                  accountID={String(account.id)}
                  orderID={order.id ? String(order.id) : undefined}
                  onChange={onInventory}
                  address={order.address}
                  referenceMovers={order.movers}
                  onCuftCalculation={onEstimateCubicFootage}
                  onMoverCountUpdate={onMoverCountUpdate}
                  subtype={order.subtype}
                />
                <hr />
              </>
            )}

            {warehouseReturn() && (
              <>
                <h3 className="text-thin">Which Warehouse Return Service Type?</h3>
                <WarehouseReturnServiceTypeSelector
                  onChange={onWarehouseReturnServiceTypeSelection}
                  serviceType={order.service_type}
                />
                <hr />
              </>
            )}

            <OrderSharedFields
              order={asOrderFormOrder(order)}
              account={asOrderFormAccount(account)}
              onboardingDate={onboardingDate}
              warehouseReturn={warehouseReturn()}
              externalMovers={order.movers}
              externalRegion={order.region}
              onOrderChange={bumpRender}
            />
          </Panel.Body>
        </Panel>

        {showSubscribePanel && (
          <SubscribePanel
            zip={order.address?.zip}
            cuft={estimatedCUFT != null ? estimatedCUFT : order.estimated_cuft}
            accountID={Number(account.id)}
            onChange={onSubscribe}
            reonboard={isReonboarding()}
          />
        )}

        {formType === 'return' && (
          <>
            {showReturnItemAlert && (
              <div className="alert alert-info">
                {!isAllowedToAddItemsToEnroute && (
                  <div className="text-center">
                    Items can only be removed (not added) because the order is <strong>{order.state}</strong>. Removed
                    items not on pallets will generate a restock. Once items are removed they cannot be re-added!
                  </div>
                )}
                {isAllowedToAddItemsToEnroute && order.state === 'enroute' && (
                  <div className="text-center">
                    Please ensure item can physically be loaded onto a truck when adding item to the order that is{' '}
                    {order.state}
                  </div>
                )}
              </div>
            )}

            <InventoryFetcher
              accountID={Number(account.id)}
              orderID={order.id ? Number(order.id) : undefined}
              selections={order.items}
              onSelect={onItems}
            />

            <FinalReturnCancellationPanel
              cancelable={!!order.allItemsSelected}
              cancelReason={order.account_cancel_intent?.reason}
              setAccountCancelIntentReason={setAccountCancelIntentReason}
              clearAccountCancelIntentReason={clearAccountCancelIntentReason}
              setCancelAccount={setCancelAccount}
            />
          </>
        )}

        <Panel>
          {order.type !== 'retail-delivery' && (
            <Panel.Body>
              {order.type !== 'selfstorage-shuttle' && !warehouseReturn() && (
                <CancellationDisclaimer
                  accountID={String(account.id)}
                  orderType={order.type}
                  orderSubtype={order.subtype}
                  serviceType={order.service_type}
                  checked={disclaimer}
                  setDisclaimer={(checked) => {
                    setDisclaimer(checked);
                    bumpRender();
                  }}
                />
              )}
              {!!order.unpaid_materials_bundle_amount && (
                <label>
                  <input
                    type="checkbox"
                    checked={disclaimerForMaterialsBundle}
                    onChange={(event) => {
                      setDisclaimerForMaterialsBundle(event.target.checked);
                      bumpRender();
                    }}
                  />
                  I agree to pay <Currency value={order.unpaid_materials_bundle_amount} /> for unlimited packing
                  materials.
                </label>
              )}
              {order.cancel_account && (
                <OrderTermContractsDisclaimer
                  accountID={String(account.id)}
                  orderScheduledDate={order.scheduled}
                  setIsEarlyTermination={setIsEarlyTermination}
                  setDisclaimer={(value) => {
                    setEarlyTerminationDisclaimer(value);
                    return value;
                  }}
                />
              )}
            </Panel.Body>
          )}

          <Panel.Footer align="right">
            {!order.id && (
              <span className="pull-left">
                <EstimatorSignatureAffirmation
                  onCheck={(checked) => {
                    setEstimatorAffirmation(checked);
                    bumpRender();
                  }}
                />
              </span>
            )}
            {order.type === 'moving-move' ? (
              <NavLinkButton to={orderURL({ id: order.id, account: { id: account.id } })}>Cancel</NavLinkButton>
            ) : (
              <NavLinkButton to={accountURL({ id: account.id })}>Cancel</NavLinkButton>
            )}
            <input
              type="submit"
              className="btn btn-primary"
              value={submitLabel}
              disabled={!savable || saving}
              onClick={(event) => {
                event.preventDefault();
                handleSave();
              }}
            />
          </Panel.Footer>
        </Panel>
      </form>
    </>
  );
};

export const OrderPickupEdit: React.FC = () => {
  useTitle('Pickup');
  const [external] = useBooleanURLSearchParam('external', true);
  const [onboardingDate] = useURLSearchParam('onboarding_date');
  const { accountID, orderID } = useParams<{ accountID: string; orderID: string }>();
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const { response: responseForOrderRequest } = useAccountPickupRequest(accountID, orderID);
  const account = responseForAccountRequest?.data;
  const order = responseForOrderRequest?.data;
  if (!account) return <Spinner />;
  if (!order) return <Spinner />;

  return (
    <OrderForm onboardingDate={onboardingDate} account={account} order={order} external={external} formType="pickup" />
  );
};

export const OrderPickupNew: React.FC = () => {
  useTitle('Pickup');
  const [external] = useBooleanURLSearchParam('external', true);
  const [onboardingDate] = useURLSearchParam('onboarding_date');
  const { accountID } = useParams<{ accountID: string }>();
  const order = useMemo(
    () => ({
      account_id: accountID,
      external,
    }),
    [accountID, external],
  );
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const account = responseForAccountRequest?.data;
  if (!account) return <Spinner />;

  return (
    <OrderForm onboardingDate={onboardingDate} account={account} order={order} external={external} formType="pickup" />
  );
};

export const OrderReturnEdit: React.FC = () => {
  useTitle('Return');
  const [external] = useBooleanURLSearchParam('external', true);
  const [warehouseReturn] = useBooleanURLSearchParam('warehouse_return');
  const { accountID, orderID } = useParams<{ accountID: string; orderID: string }>();
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const { response: responseForOrderRequest } = useAccountReturnRequest(accountID, orderID);
  const account = responseForAccountRequest?.data;
  const order = responseForOrderRequest?.data;
  if (!account) return <Spinner />;
  if (!order) return <Spinner />;

  return (
    <OrderForm
      account={account}
      order={order}
      external={external}
      warehouseReturn={warehouseReturn}
      formType="return"
    />
  );
};

export const OrderReturnNew: React.FC = () => {
  useTitle('Return');
  const [external] = useBooleanURLSearchParam('external', true);
  const [warehouseReturn] = useBooleanURLSearchParam('warehouse_return');
  const { accountID } = useParams<{ accountID: string }>();
  const order = useMemo(
    () => ({
      account_id: accountID,
      external,
    }),
    [accountID, external],
  );
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const account = responseForAccountRequest?.data;
  if (!account) return <Spinner />;

  return (
    <OrderForm
      account={account}
      order={order}
      external={external}
      warehouseReturn={warehouseReturn}
      formType="return"
    />
  );
};

export const OrderMoveForm: React.FC = () => {
  useTitle('Move');
  const { accountID, orderID } = useParams<{ accountID: string; orderID: string }>();
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const { response: responseForOrderRequest } = useAccountMoveRequest(accountID, orderID);
  const account = responseForAccountRequest?.data;
  const order = responseForOrderRequest?.data;

  if (!account) return <Spinner />;
  if (!order) return <Spinner />;
  return <OrderForm account={account} order={order} formType="move" />;
};

export const OrderRetailDeliveryForm: React.FC = () => {
  useTitle('Retail Delivery');
  const { accountID, orderID } = useParams<{ accountID: string; orderID: string }>();
  const { response: responseForAccountRequest } = useAccountRequest(accountID);
  const { response: responseForAccountRetailDeliveryRequest } = useAccountRetailDeliveryRequest(accountID, orderID);
  const account = responseForAccountRequest?.data;
  const order = responseForAccountRetailDeliveryRequest?.data;

  if (!account) return <Spinner />;
  if (!order) return <Spinner />;

  return <OrderForm account={account} order={order} formType="retail-delivery" />;
};
